import { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from '@mui/material';
import { Download, Search } from '@mui/icons-material';
import api from '../../api/client';
import { formatDateShort, formatTime24, ensureTimezone } from '../../utils/time';

interface ChatMsg {
  id: number;
  created_at: string;
  username: string;
  full_name: string;
  position_name: string;
  body: string;
}

export default function ChatLogPage() {
  const [rows, setRows] = useState<ChatMsg[]>([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [userQ, setUserQ] = useState('');
  const [textQ, setTextQ] = useState('');

  const params = useCallback(() => {
    const p: Record<string, string> = {};
    if (start) p.start = start;
    if (end) p.end = end + 'T23:59:59';
    if (userQ) p.user_q = userQ;
    if (textQ) p.text_q = textQ;
    return p;
  }, [start, end, userQ, textQ]);

  const load = useCallback(() => {
    api.get('/chat/log', { params: params() }).then((r) => setRows(r.data)).catch(() => {});
  }, [params]);

  useEffect(() => {
    ensureTimezone().finally(load);
  }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get('/chat/log/export', { params: params(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'chat_log.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Dispatch Chat Log</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'flex-end' } }}>
          <TextField label="From" type="date" size="small" slotProps={{ inputLabel: { shrink: true } }} value={start} onChange={(e) => setStart(e.target.value)} />
          <TextField label="To" type="date" size="small" slotProps={{ inputLabel: { shrink: true } }} value={end} onChange={(e) => setEnd(e.target.value)} />
          <TextField label="User" size="small" value={userQ} onChange={(e) => setUserQ(e.target.value)} />
          <TextField label="Message text" size="small" value={textQ} onChange={(e) => setTextQ(e.target.value)} />
          <Button variant="contained" startIcon={<Search />} onClick={load}>Search</Button>
          <Button variant="outlined" startIcon={<Download />} onClick={exportCsv}>Export CSV</Button>
        </Box>
      </Paper>
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No messages found.</Typography></TableCell></TableRow>
            )}
            {rows.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.created_at ? `${formatDateShort(m.created_at)} ${formatTime24(m.created_at)}` : '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.full_name || m.username}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.position_name || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
